import axios, { AxiosRequestConfig } from 'axios';
import qs from 'qs';

import { AxiosServiceResponse, PagedList } from 'interfaces/general';

import type {
  GetUnreadParams,
  SendReadMessageBody,
  UnreadNotification
} from './types';

const instance = axios.create({
  baseURL: `${process.env.MESSAGE_COMMUNICATION_SERVICE_ENDPOINT}/api/v1`,
  headers: {
    // SE ELIMINARIA CUANDO SE TENGA QA ESTABLE API KEY
    // 'Api-key': process.env.REQUEST_HEADER_API_KEY,
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

export interface MexicanPostalCodeRequest {
  stateName: string;
}

export interface MexicanPostalCodeResponse {
  responseDetail: [
    {
      countryStateName: string;
      totalDataExternal: number;
      totalDataDB: number;
      totalPostalCodesCreated: number;
      totalPostalCodesInactivated: number;
      totalMunicipalitiesCreated: number;
      totalLocalitiesCreated: number;
      totalPostalCodeAddressesUpdated: number;
    }
  ];
}

export interface GetMexicanPostalCodeResponse {
  key: string;
  value: string;
}

export interface SearchNotificationsRequest {
  text?: string;
  pageNumber?: number;
  pageSize?: number;
  orderColumn?: string;
  descending?: boolean;
  isRead?: boolean;
  dateFrom?: Date | string;
  dateTo?: Date | string;
  services?: number[];
  clients?: number[];
  workforces?: number[];
}

export interface SearchNotificationResponse {
  id: number;
  ticketId: number;
  userFirstName: string;
  userFirstLastname: string;
  userSecondLastName: string;
  clientId: number;
  clientName: string;
  serviceId: number;
  serviceName: string;
  isRead: boolean;
  createdDateTime: Date;
  message: string;
  title: string;
}

export interface IncidentRequirementRequest {
  issueDescription: string;
  locationId: number | null;
  email: string;
  phone: string;
}

export interface NotificationsGeneralSearchParams {
  NotificationId?: number;
  Title?: string;
  Body?: string;
  LanguageId?: number;
  Text?: string;
  PageNumber?: number;
  PageSize?: number;
  OrderColumn?: string;
  Descending?: boolean;
}

export interface NotificationsGeneralSearchResponse {
  notificationId?: number;
  notificationKey?: string;
  title?: string;
  body?: string;
  languageId?: number;
}

export interface GetAllMessageParams {
  messageParamId?: number;
  name?: string;
}

export interface EditNotification {
  notificationId?: number;
  title?: string;
  body?: string;
  languageId?: number;
  messageParamsId?: number[];
}

export default {
  instance,
  notifications: {
    /**
     * Retrieves unread notifications for a user.
     *
     * @tags Notifications
     * @name getUnread
     * @request GET: /api/v1/Notifications/me/GetUnread
     * @secure
     */
    getUnread: (params: GetUnreadParams, config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<UnreadNotification>>
      >({
        url: `/Notifications/me/GetUnread`,
        method: 'GET',
        params,
        ...config
      }),

    /**
     * Retrieves unread notifications for a user.
     *
     * @tags Notifications
     * @name getAllNotificationsByUser
     * @request GET: /api/v1/Notifications/Get
     * @secure
     */
    getAllNotificationsByUser: (params: any, config: any = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<UnreadNotification>>
      >({
        url: `/Notifications/Get`,
        method: 'GET',
        params,
        ...config
      }),

    /**
     * Retrieves unread notifications for a user.
     *
     * @tags Notifications
     * @name deleteNotifications
     * @request GET: /api/v1/Notifications/Delete
     * @secure
     */
    deleteNotifications: (params: any, config: any = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<UnreadNotification>>
      >({
        url: `/Notifications/Delete`,
        method: 'DELETE',
        params,
        ...config
      }),

    /**
     * Send read message.
     *
     * @tags Notifications
     * @name sendReadMessage
     * @request PUT: /api/v1/Notifications/me/Read
     * @secure
     */
    sendReadMessage: (
      body: SendReadMessageBody,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<{ message: string }>>({
        url: `/Notifications/me/Read`,
        method: 'PUT',
        data: body,
        ...config
      }),

    /**
     * Recibe notificaciones de escalamiento de ticket .
     *
     * @tags Notifications
     * @name SearchEscalations
     * @request POST: /api/v1/Notifications/SearchEscalations
     * @secure
     */
    SearchEscalations: (
      data: SearchNotificationsRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<SearchNotificationResponse[]>>
      >({
        url: `/Notifications/SearchEscalations`,
        method: 'POST',
        data,
        ...config
      })
  },
  Locations: {
    /**
     * Nombre de los estados de Mexico externos a mexico
     *
     * @tags Location
     * @name MexicanPostalCode
     * @request POST: /api/v1/Location/MexicanPostalCode
     * @secure
     */
    MexicanPostalCode: (
      data: MexicanPostalCodeRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<MexicanPostalCodeResponse>>({
        url: `/Location/MexicanPostalCode`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Nombre de los estados de Mexico externos a mexico
     *
     * @tags Location
     * @name ExternalMexicanStates
     * @request POST: /api/v1/Location/ExternalMexicanStates
     * @secure
     */
    ExternalMexicanStates: (config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<GetMexicanPostalCodeResponse[]>
      >({
        url: `/Location/ExternalMexicanStates`,
        method: 'POST',
        ...config
      })
  },
  Public: {
    /**
     * Solicitudes publicas de incidentes en ubcicaciones
     *
     * @tags Public
     * @name IncidentRequirement
     * @request POST: /api/v1/Public/IncidentRequirement
     * @secure
     */
    IncidentRequirement: (
      data: IncidentRequirementRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<IncidentRequirementRequest>>({
        url: `/Public/IncidentRequirement`,
        method: 'POST',
        data,
        ...config
      })
  },
  getNotfications: {
    /**
     * Trae todas la notificaciones del sistema
     *
     * @tags Public
     * @name getAllNotifications
     * @request GET: /api/v1/Notifications/GeneralSearch
     * @secure
     */
    getAllNotifications: (
      params: NotificationsGeneralSearchParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<NotificationsGeneralSearchResponse>>
      >({
        url: `/Notifications/GeneralSearch`,
        method: 'GET',
        params,
        paramsSerializer: (prms) =>
          qs.stringify(prms, { arrayFormat: 'repeat' }),
        ...config
      }),
    /**
     * Trae todos los parametros de las notificaciones
     *
     * @tags Public
     * @name getParamsNotificationById
     * @request GET: /api/v1/Notifications/GetMessageParamsById
     * @secure
     */
    getParamsNotificationById: (
      params: {
        NotificationId?: number;
        Text?: string;
        PageNumber?: number;
        PageSize?: number;
        OrderColumn?: string;
        Descending?: boolean;
      },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<GetAllMessageParams>>
      >({
        url: `/Notifications/GetMessageParamsById`,
        method: 'GET',
        params,
        paramsSerializer: (prms) =>
          qs.stringify(prms, { arrayFormat: 'repeat' }),
        ...config
      }),
    /**
     * Trae todos los parametros de las notificaciones
     *
     * @tags Public
     * @name getAllParamsNotification
     * @request GET: /api/v1/Notifications/GetAllMessageParams
     * @secure
     */
    getAllParamsNotification: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<GetAllMessageParams[]>>({
        url: `/Notifications/GetAllMessageParams`,
        method: 'GET',
        ...config
      }),
    /**
     * Edita la notificacion
     *
     * @tags Public
     * @name editNotification
     * @request PUT: /api/v1/Notifications/PutNotification
     * @secure
     */
    editNotification: (
      data: EditNotification,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Notifications/PutNotification`,
        method: 'PUT',
        data,
        ...config
      })
  }
};
